import type { Summary } from './types';

export function renderNumericSummary(summary: Summary, options?: { hideStats?: boolean }): HTMLElement {
  const values = summary.values ?? [];
  const sorted = [...values].sort((a, b) => a - b);
  const n = values.length;
  
  if (n === 0) {
    const empty = document.createElement('div');
    empty.className = 'numeric-summary';
    empty.textContent = 'No data available';
    return empty;
  }

  // Calculate statistics
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const mean = values.reduce((acc, value) => acc + value, 0) / n;
  const median = summary.median ?? sorted[Math.floor(n / 2)];
  const variance = values.reduce((acc, value) => acc + Math.pow(value - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);

  // Create container
  const container = document.createElement('div');
  container.className = 'numeric-histogram';

  // Create histogram
  const histogramContainer = document.createElement('div');
  histogramContainer.className = 'histogram-chart';

  // Calculate histogram bins (fixed 30 bins)
  const binCount = 30;
  const range = max - min || 1;
  const binWidth = range / binCount;
  
  // Create bins
  const bins: number[] = new Array(binCount).fill(0);
  values.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / binWidth), binCount - 1);
    bins[binIndex]++;
  });
  
  const maxBinCount = Math.max(...bins);

  // Create SVG for histogram with distribution curves
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('viewBox', '0 0 600 350');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('class', 'histogram-svg');

  const chartPadding = { top: 20, right: 20, bottom: 35, left: 50 };
  const chartWidth = 600 - chartPadding.left - chartPadding.right;
  const chartHeight = 350 - chartPadding.top - chartPadding.bottom;

  // Draw histogram bars
  const barWidth = chartWidth / binCount;
  bins.forEach((count, i) => {
    const barHeight = maxBinCount > 0 ? (count / maxBinCount) * chartHeight : 0;
    const x = chartPadding.left + i * barWidth;
    const y = chartPadding.top + chartHeight - barHeight;
    
    const rect = document.createElementNS(svgNS, 'rect');
    rect.setAttribute('x', String(x + 1));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(barWidth - 2));
    rect.setAttribute('height', String(barHeight));
    rect.setAttribute('class', 'histogram-bar');
    svg.appendChild(rect);
  });

  // Log-normal distribution PDF (excellent for income/salary data)
  const calcLogNormalPDF = (x: number, mu: number, sigma: number): number => {
    if (x <= 0 || sigma <= 0) return 0;
    const logX = Math.log(x);
    const pdf = (1 / (x * sigma * Math.sqrt(2 * Math.PI))) * 
                Math.exp(-Math.pow(logX - mu, 2) / (2 * sigma * sigma));
    return pdf;
  };

  // Shift data by minimum so log-normal distribution starts at 0
  const shiftedValues = values.map(v => v - min + 0.001 * range);
  const shiftedMean = shiftedValues.reduce((a, b) => a + b, 0) / n;
  const shiftedVariance = shiftedValues.reduce((a, v) => a + Math.pow(v - shiftedMean, 2), 0) / n;

  // Log-normal parameters from shifted data
  const logNormalSigmaSq = Math.log(1 + shiftedVariance / (shiftedMean * shiftedMean));
  const logNormalSigma = Math.sqrt(logNormalSigmaSq);
  const logNormalMu = Math.log(shiftedMean) - logNormalSigmaSq / 2;

  // Calculate SSE for both distributions to pick the best fit
  let normalSSE = 0;
  let logNormalSSE = 0;
  
  for (let i = 0; i < binCount; i++) {
    const binMid = min + (i + 0.5) * binWidth;
    
    // Normal expected count
    const z = (binMid - mean) / stdDev;
    const normalPDF = (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
    const normalExpected = normalPDF * n * binWidth;
    normalSSE += Math.pow(bins[i] - normalExpected, 2);
    
    // Log-normal expected count
    const shiftedX = binMid - min + 0.001 * range;
    const logNormalPDF = calcLogNormalPDF(shiftedX, logNormalMu, logNormalSigma);
    const logNormalExpected = logNormalPDF * n * binWidth;
    logNormalSSE += Math.pow(bins[i] - logNormalExpected, 2);
  }

  // Choose the better fitting distribution
  const useLogNormal = logNormalSSE < normalSSE && isFinite(logNormalSSE);
  const distributionLabel = useLogNormal ? 'Log-normal' : 'Normal';

  // Draw the best-fitting distribution curve
  if (stdDev > 0) {
    const numPoints = 100;
    const curvePoints: string[] = [];
    
    for (let i = 0; i <= numPoints; i++) {
      const x = min + (i / numPoints) * range;
      let pdf: number;
      
      if (useLogNormal) {
        const shiftedX = x - min + 0.001 * range;
        pdf = calcLogNormalPDF(shiftedX, logNormalMu, logNormalSigma);
      } else {
        const z = (x - mean) / stdDev;
        pdf = (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
      }
      
      const expectedCount = pdf * n * binWidth;
      const svgX = chartPadding.left + (i / numPoints) * chartWidth;
      const svgY = chartPadding.top + chartHeight - (expectedCount / maxBinCount) * chartHeight;
      
      if (isFinite(svgY)) {
        curvePoints.push(`${i === 0 ? 'M' : 'L'}${svgX},${Math.max(chartPadding.top, svgY)}`);
      }
    }
    
    if (curvePoints.length > 1) {
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d', curvePoints.join(' '));
      path.setAttribute('class', 'distribution-curve');
      svg.appendChild(path);
    }
    
    // Add distribution label to the bottom left, just before the curve starts
    const label = document.createElementNS(svgNS, 'text');
    label.setAttribute('x', String(chartPadding.left - 4));
    label.setAttribute('y', String(chartPadding.top + chartHeight - 14));
    label.setAttribute('text-anchor', 'end');
    label.setAttribute('class', 'distribution-label');
    
    const line1 = document.createElementNS(svgNS, 'tspan');
    line1.setAttribute('x', String(chartPadding.left - 4));
    line1.setAttribute('dy', '0');
    line1.textContent = distributionLabel;
    label.appendChild(line1);
    
    const line2 = document.createElementNS(svgNS, 'tspan');
    line2.setAttribute('x', String(chartPadding.left - 4));
    line2.setAttribute('dy', '1.2em');
    line2.textContent = 'distribution';
    label.appendChild(line2);
    
    svg.appendChild(label);
  }

  // Helper to format with commas and round to whole number
  const formatWithCommas = (v: number, prefix?: string) => {
    const formatted = Math.round(v).toLocaleString();
    return prefix ? `${prefix}${formatted}` : formatted;
  };
  const valuePrefix = summary.histogram_number_prefix;

  // Draw mean line
  const meanX = chartPadding.left + ((mean - min) / range) * chartWidth;
  const meanLine = document.createElementNS(svgNS, 'line');
  meanLine.setAttribute('x1', String(meanX));
  meanLine.setAttribute('y1', String(chartPadding.top));
  meanLine.setAttribute('x2', String(meanX));
  meanLine.setAttribute('y2', String(chartPadding.top + chartHeight));
  meanLine.setAttribute('class', 'stat-line mean-line');
  svg.appendChild(meanLine);

  // Draw median line
  const medianX = chartPadding.left + ((median - min) / range) * chartWidth;
  const medianLine = document.createElementNS(svgNS, 'line');
  medianLine.setAttribute('x1', String(medianX));
  medianLine.setAttribute('y1', String(chartPadding.top));
  medianLine.setAttribute('x2', String(medianX));
  medianLine.setAttribute('y2', String(chartPadding.top + chartHeight));
  medianLine.setAttribute('class', 'stat-line median-line');
  svg.appendChild(medianLine);

  // Add bubble labels for mean and median on the chart using foreignObject for CSS styling
  const meanOnLeft = mean <= median;
  
  const createBubbleLabel = (
    x: number, 
    labelText: string, 
    valueText: string, 
    colorClass: string,
    onLeft: boolean
  ) => {
    const fo = document.createElementNS(svgNS, 'foreignObject');
    // Start with large container, position will be adjusted after measuring
    fo.setAttribute('x', '0');
    fo.setAttribute('y', String(chartPadding.top - 4));
    fo.setAttribute('width', '200');
    fo.setAttribute('height', '100');
    fo.setAttribute('class', 'stat-bubble-fo');
    fo.setAttribute('data-line-x', String(x));
    fo.setAttribute('data-on-left', String(onLeft));
    
    const div = document.createElement('div');
    div.className = `stat-bubble-html ${colorClass}-bubble-html`;
    div.innerHTML = `<span class="stat-bubble-label-html">${labelText}</span><span class="stat-bubble-value-html">${valueText}</span>`;
    fo.appendChild(div);
    
    return fo;
  };
  
  const meanBubble = createBubbleLabel(
    meanX, 'Mean', formatWithCommas(mean, valuePrefix), 'mean', meanOnLeft
  );
  const medianBubble = createBubbleLabel(
    medianX, 'Median', formatWithCommas(median, valuePrefix), 'median', !meanOnLeft
  );
  svg.appendChild(meanBubble);
  svg.appendChild(medianBubble);
  
  // Position bubbles after render to measure actual sizes
  const positionBubbles = () => {
    const positions: Array<{ fo: SVGForeignObjectElement; x: number; width: number }> = [];
    [meanBubble, medianBubble].forEach(fo => {
      const div = fo.querySelector('.stat-bubble-html') as HTMLElement;
      if (!div) return;
      const lineX = parseFloat(fo.getAttribute('data-line-x') || '0');
      const onLeft = fo.getAttribute('data-on-left') === 'true';
      const actualWidth = div.offsetWidth;
      const gap = 4;
      const bubbleX = onLeft
        ? Math.max(0, lineX - actualWidth - gap)
        : lineX + gap;
      fo.setAttribute('x', String(bubbleX));
      fo.style.visibility = '';
      positions.push({ fo, x: bubbleX, width: actualWidth });
    });

    // If the two bubbles overlap horizontally, hide the one rendered first (mean)
    if (positions.length === 2) {
      const [a, b] = positions;
      const overlap = a.x < b.x + b.width && a.x + a.width > b.x;
      if (overlap) positions[0].fo.style.visibility = 'hidden';
    }
  };
  
  // Position after initial render and on resize
  requestAnimationFrame(positionBubbles);
  window.addEventListener('resize', positionBubbles);

  // X-axis labels (6 labels total)
  const labelCount = 6;
  for (let i = 0; i < labelCount; i++) {
    const value = min + (i / (labelCount - 1)) * range;
    const x = chartPadding.left + (i / (labelCount - 1)) * chartWidth;
    const text = document.createElementNS(svgNS, 'text');
    text.setAttribute('x', String(x));
    text.setAttribute('y', String(chartPadding.top + chartHeight + 20));
    text.setAttribute('class', 'axis-label');
    text.textContent = formatWithCommas(value, valuePrefix);
    svg.appendChild(text);
  }

  // Create hover elements for percentile display
  const hoverLine = document.createElementNS(svgNS, 'line');
  hoverLine.setAttribute('class', 'hover-line');
  hoverLine.setAttribute('y1', String(chartPadding.top));
  hoverLine.setAttribute('y2', String(chartPadding.top + chartHeight));
  hoverLine.style.display = 'none';
  svg.appendChild(hoverLine);

  // Create tooltip using foreignObject for CSS-driven sizing
  const tooltipFo = document.createElementNS(svgNS, 'foreignObject');
  tooltipFo.setAttribute('class', 'hover-tooltip-fo');
  tooltipFo.setAttribute('width', '250');
  tooltipFo.setAttribute('height', '120');
  tooltipFo.style.display = 'none';
  
  const tooltipDiv = document.createElement('div');
  tooltipDiv.className = 'hover-tooltip-html';
  tooltipDiv.innerHTML = '<span class="hover-tooltip-value-html"></span><span class="hover-tooltip-percentile-html"></span>';
  tooltipFo.appendChild(tooltipDiv);
  
  svg.appendChild(tooltipFo);

  // Invisible overlay rect for capturing mouse/touch events
  const overlay = document.createElementNS(svgNS, 'rect');
  overlay.setAttribute('x', String(chartPadding.left));
  overlay.setAttribute('y', String(chartPadding.top));
  overlay.setAttribute('width', String(chartWidth));
  overlay.setAttribute('height', String(chartHeight));
  overlay.setAttribute('fill', 'transparent');
  overlay.setAttribute('class', 'histogram-overlay');
  svg.appendChild(overlay);

  // Build list of populated bins with their cumulative percentiles for smooth interpolation
  const populatedBins: Array<{ x: number; pct: number }> = [];
  let cumulative = 0;
  
  // Add starting point
  populatedBins.push({ x: min, pct: 0 });
  
  for (let i = 0; i < binCount; i++) {
    if (bins[i] > 0) {
      cumulative += bins[i];
      // Use the right edge of populated bins as the x position
      const binRightEdge = min + (i + 1) * binWidth;
      populatedBins.push({ x: binRightEdge, pct: (cumulative / n) * 100 });
    }
  }
  
  // Ensure we end at 100%
  if (populatedBins[populatedBins.length - 1].pct < 100) {
    populatedBins.push({ x: max, pct: 100 });
  }

  // Function to calculate percentile by interpolating between populated bins
  const getPercentile = (xValue: number): number => {
    if (xValue <= min) return 0;
    if (xValue >= max) return 100;
    
    // Find the two populated bin boundaries this value falls between
    let lowerIdx = 0;
    for (let i = 0; i < populatedBins.length - 1; i++) {
      if (populatedBins[i].x <= xValue && xValue < populatedBins[i + 1].x) {
        lowerIdx = i;
        break;
      }
    }
    
    const lower = populatedBins[lowerIdx];
    const upper = populatedBins[lowerIdx + 1];
    
    // Linear interpolation between the two populated bin boundaries
    const t = (xValue - lower.x) / (upper.x - lower.x);
    return lower.pct + t * (upper.pct - lower.pct);
  };

  // Function to update hover display
  const updateHover = (clientX: number, clientY: number) => {
    const svgRect = svg.getBoundingClientRect();
    const svgX = (clientX - svgRect.left) / svgRect.width * 600;
    
    // Check if within chart area
    if (svgX < chartPadding.left || svgX > chartPadding.left + chartWidth) {
      hoverLine.style.display = 'none';
      tooltipFo.style.display = 'none';
      return;
    }
    
    // Calculate x value from position
    const xRatio = (svgX - chartPadding.left) / chartWidth;
    const xValue = min + xRatio * range;
    const percentile = getPercentile(xValue);
    
    // Update hover line
    hoverLine.setAttribute('x1', String(svgX));
    hoverLine.setAttribute('x2', String(svgX));
    hoverLine.style.display = '';
    
    // Update tooltip content
    const valueText = formatWithCommas(xValue, valuePrefix);
    const percentileText = `${percentile.toFixed(1)}th percentile`;
    
    const valueSpan = tooltipDiv.querySelector('.hover-tooltip-value-html');
    const pctSpan = tooltipDiv.querySelector('.hover-tooltip-percentile-html');
    if (valueSpan) valueSpan.textContent = valueText;
    if (pctSpan) pctSpan.textContent = percentileText;
    
    // Position tooltip based on actual rendered size
    tooltipFo.style.display = '';
    const tooltipWidth = tooltipDiv.offsetWidth;
    const gap = 8;
    const chartRight = chartPadding.left + chartWidth;
    
    let tooltipX: number;
    if (svgX + gap + tooltipWidth > chartRight) {
      // Flip to left of line
      tooltipX = svgX - gap - tooltipWidth;
    } else {
      // Right of line
      tooltipX = svgX + gap;
    }
    if (tooltipX < 2) tooltipX = 2;
    const tooltipY = chartPadding.top + 10;
    
    tooltipFo.setAttribute('x', String(tooltipX));
    tooltipFo.setAttribute('y', String(tooltipY));
  };

  const hideHover = () => {
    hoverLine.style.display = 'none';
    tooltipFo.style.display = 'none';
  };

  // Mouse events
  overlay.addEventListener('mousemove', (e) => updateHover(e.clientX, e.clientY));
  overlay.addEventListener('mouseleave', hideHover);
  
  // Touch events for mobile
  overlay.addEventListener('touchmove', (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    updateHover(touch.clientX, touch.clientY);
  }, { passive: false });
  overlay.addEventListener('touchend', hideHover);

  histogramContainer.appendChild(svg);
  container.appendChild(histogramContainer);

  // Stats panel
  if (!options?.hideStats) {
    const stats = document.createElement('div');
    stats.className = 'histogram-stats';

    const statItems: Array<[string, number, string?]> = [
      ['Mean', mean, 'mean'],
      ['Median', median, 'median'],
      ['Min', min],
      ['Max', max],
    ];

    statItems.forEach(([label, value, cssClass]) => {
      const row = document.createElement('div');
      row.className = 'stat-row' + (cssClass ? ` stat-${cssClass}` : '');
      row.innerHTML = `<span class="stat-label">${label}</span><span class="stat-value">${formatWithCommas(value, valuePrefix)}</span>`;
      stats.appendChild(row);
    });

    container.appendChild(stats);
  }

  return container;
}
